import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { GuideLink } from '../common';
import SubmitRevisionControl from './SubmitRevisionControl';
import ChangeSpecPanel from './ChangeSpecPanel';
import { ROLE_GUIDES } from '../../utils';
import DocumentViewer from './DocumentViewer';
import DeadlinePill from './DeadlinePill';
import MarkStepCompleteButton from './MarkStepCompleteButton';
import ReviewSequenceIndicator from './ReviewSequenceIndicator';
import ProducerReadbackPanel from './ProducerReadbackPanel';
import ConfirmFixesPanel from './ConfirmFixesPanel';
import { readDraft, writeDraft, clearDraft } from './assessmentDraft';
import { renderWithMentions, MentionTextarea, mentionsViewer } from './mentions';
import { RISK_OPTIONS } from './constants';
import CommentEditForm from './CommentEditForm';
import type { User, CycleWithComments, ReviewComment, ReviewCycle, RiskLevel } from './types';
import { DISPOSITION_LABELS, carriesDisposition } from './types';
import RevisionBanner from './RevisionBanner';
import './ContentReview.css';

import { STEP_LABEL, isNamedProducer as viewerIsNamedProducer, reviewerHandoverStep, isAssignedReviewer } from './reviewStage';

interface ReviewInterfaceProps {
  user: User | null;
  cycleId: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
  // 2026-08-20 — accepts an optional flash message so the dashboard can say
  // what just happened. Callers passing a plain () => void still type-check.
  onBack: (flash?: string) => void;
  // Phase 12 fix pass (Batch B) — edit-lock + step-complete widgets in
  // the reviewer's own view. Same shape as DecisionView. Props default
  // to conservative values so legacy call sites without these props
  // still render (widgets self-gate on cycle state + user step).
  currentUserStep?: 'medical' | 'legal' | 'producer' | null;
  // Mentioned participant (D69, 2026-08-21): read everything, reply, ask for
  // input, resolve threads — no findings, assessment, lock or step actions.
  participant?: boolean;
  // 2026-09-01 — admin view switcher's Producer view: force the producer's
  // page shape (read-back panel, reply-only composer, trimmed comments) for
  // a viewer who is not the cycle's named producer. Page simulation only —
  // backend gates are untouched.
  simulateProducer?: boolean;
  // Decision makers may edit anyone's comment; everyone else only their own.
  // Mirrors the backend rule on PUT /comments/:id (author OR decision_maker).
  canEditAnyComment?: boolean;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

type CommentVisibility = 'mine' | 'all' | 'others';

const ReviewInterface: React.FC<ReviewInterfaceProps> = ({
  user, cycleId, getAuthHeaders, onBack,
  currentUserStep = null, participant = false, simulateProducer = false,
  canEditAnyComment = false
}) => {
  const [data, setData] = useState<CycleWithComments | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [childComments, setChildComments] = useState<ReviewComment[]>([]);
  // Phase 11.2 — full child cycle data keyed by id, populated alongside child
  // comments so the per-child "Mark as reviewed" panel can read reviewedBy.
  const [childCycles, setChildCycles] = useState<Record<string, ReviewCycle>>({});
  // Active tab in DocumentViewer — owned here so the side panel can switch
  // between parent assessment form and per-child mark-reviewed panel.
  const [activeTab, setActiveTab] = useState<string>('parent');
  const [markingReviewed, setMarkingReviewed] = useState(false);
  // Bumped every time the cycle is refetched, so children rendered inside
  // DocumentViewer (the child tabs and their reviewed captions) re-read the
  // server instead of showing whatever was true at mount (2026-08-21).
  const [dataVersion, setDataVersion] = useState(0);

  // 3-state comment visibility (Phase 6A) originally defaulted to Mine only, to
  // reduce anchoring bias — a reviewer should form their own assessment before
  // reading everyone else's.
  //
  // Changed to All on 2026-08-20 (Fredrik, from the prod E2E as Rita). The
  // bias argument only ever covered OTHER REVIEWERS' opinions; it did not
  // account for @mentions, which are addressed TO the reader and expect a
  // reply. Under the Mine default those were invisible by construction: a
  // reviewer opening a cycle where a colleague had asked them a direct
  // question saw an empty comment list and no indication one existed. A
  // question you never see is worse than an opinion you see too early.
  const [commentVisibility, setCommentVisibility] = useState<CommentVisibility>('all');

  // Assessment form — reviewers set a risk level only; decisions are made by the decision maker
  const [riskLevel, setRiskLevel] = useState<RiskLevel | ''>('');
  const [assessmentSummary, setAssessmentSummary] = useState('');
  // Three states, not two (Fredrik, 2026-08-31). A prefilled form carries no
  // provenance: an unsent local draft and a submitted assessment render
  // identically, and he read his own months-old draft as Johanna's submitted
  // medical assessment — reasonably, because nothing on screen distinguished
  // them. `draftRestored` marks the form as holding unsent local text;
  // `editingAssessment` unlocks a SUBMITTED one, which otherwise renders as a
  // read-only card so it cannot be overwritten by a stray click.
  const [draftRestored, setDraftRestored] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(false);
  const [submittingAssessment, setSubmittingAssessment] = useState(false);
  const [assessmentError, setAssessmentError] = useState<string | null>(null);
  const [reviewCompleted, setReviewCompleted] = useState(false);

  // Distinguish initial mount from subsequent refreshes (e.g. after a comment
  // submit) so we don't blank out the whole screen and unmount DocumentViewer,
  // which would reset activeStateId and scroll position.
  const hasLoadedOnceRef = useRef(false);

  // Restore an in-progress assessment draft (see assessmentDraft.ts). Runs
  // before/independently of the fetch; a submitted assessment clears it.
  useEffect(() => {
    const draft = readDraft(cycleId, user?.email);
    if (!draft) return;
    if (draft.riskLevel) setRiskLevel(draft.riskLevel as RiskLevel);
    if (draft.summary) setAssessmentSummary(draft.summary);
    if (draft.riskLevel || draft.summary) setDraftRestored(true);
  }, [cycleId, user?.email]);

  // Keep the draft current. Skipped once the review is completed — at that
  // point the server holds the truth and a stale draft would fight it.
  useEffect(() => {
    if (reviewCompleted) return;
    writeDraft(cycleId, user?.email, { riskLevel, summary: assessmentSummary });
  }, [cycleId, user?.email, riskLevel, assessmentSummary, reviewCompleted]);

  const fetchCycle = useCallback(async () => {
    try {
      if (!hasLoadedOnceRef.current) setLoading(true);
      const headers = await getAuthHeaders();
      const res = await axios.get(`${backendUrl}/api/content-review/cycles/${cycleId}`, { headers });
      setData(res.data);

      // Fetch child review comments AND full child cycle data
      try {
        const parsedRes = await axios.get(`${backendUrl}/api/content-review/cycles/${cycleId}/parsed-content`, { headers });
        const children = parsedRes.data.children || [];
        if (children.length > 0) {
          const allChildComments: ReviewComment[] = [];
          const cycleMap: Record<string, ReviewCycle> = {};
          for (const child of children) {
            const childRes = await axios.get(`${backendUrl}/api/content-review/cycles/${child._id}`, { headers });
            cycleMap[child._id] = childRes.data.cycle;
            allChildComments.push(...(childRes.data.comments || []).map((c: ReviewComment) => ({
              ...c,
              _childName: child.contentItem.name
            })));
          }
          setChildComments(allChildComments);
          setChildCycles(cycleMap);
        } else {
          setChildCycles({});
        }
      } catch (childErr) {
        console.error('Error fetching child cycles:', childErr);
      }

      // Tell DocumentViewer its children may have changed (reviewed markers,
      // new media children) so its tabs re-read the server.
      setDataVersion(v => v + 1);

      // Pre-populate assessment if already submitted
      const myAssessment = res.data.cycle.reviewerAssessments.find(
        (a: any) => {
          return a.reviewer.email === user?.email;
        }
      );
      // Only a SUBMITTED assessment overrides what is on screen. Pre-screen
      // pre-populates stub assessments, and a refetch triggered mid-edit (e.g.
      // after marking a child reviewed) must not wipe the summary the reviewer
      // is part-way through writing (Fredrik, 2026-08-21).
      if (myAssessment?.submittedAt) {
        if (myAssessment.riskLevel) setRiskLevel(myAssessment.riskLevel);
        if (myAssessment.summary) setAssessmentSummary(myAssessment.summary);
        setReviewCompleted(true);
        setDraftRestored(false);
        clearDraft(cycleId, user?.email);
      }

      setError(null);
      hasLoadedOnceRef.current = true;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch review cycle');
    } finally {
      setLoading(false);
    }
  }, [cycleId, getAuthHeaders, user?.email]);

  useEffect(() => { fetchCycle(); }, [fetchCycle]);

  const handleToggleResolved = async (comment: ReviewComment) => {
    try {
      const headers = await getAuthHeaders();
      await axios.put(`${backendUrl}/api/content-review/comments/${comment._id}`, {
        resolved: !comment.resolved
      }, { headers });
      fetchCycle();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update comment');
    }
  };

  // Phase 11.2 — toggle "I've reviewed this" on a child cycle (typically an
  // embedded video). Reviewer must click this explicitly — comments don't
  // auto-mark, since one comment isn't proof the child is fully reviewed.
  const handleToggleChildReviewed = async (childCycleId: string, makeReviewed: boolean) => {
    try {
      setMarkingReviewed(true);
      const headers = await getAuthHeaders();
      if (makeReviewed) {
        await axios.post(`${backendUrl}/api/content-review/cycles/${childCycleId}/reviewed`, {}, { headers });
      } else {
        await axios.delete(`${backendUrl}/api/content-review/cycles/${childCycleId}/reviewed`, { headers });
      }
      await fetchCycle();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update reviewed status');
    } finally {
      setMarkingReviewed(false);
    }
  };

  // Reply to a comment thread (COMMENT_DISPOSITION_SPEC §4).
  //
  // Deliberately available to ANYONE who can see the cycle, not just
  // reviewers: the person best placed to answer "@Rita what do you think?"
  // is often a mentioned expert with no reviewer role at all, and D69 already
  // grants them cycle access precisely so they can take part. The backend
  // enforces the same rule — base reviewer/DM permission OR a mention on this
  // cycle — so this is not the gate, only the affordance.
  //
  // Errors are rethrown so the card can show them inline against the reply
  // that failed; a page-level banner would leave the user unsure which of
  // several open threads it referred to.
  const handleReplyToComment = async (rootId: string, text: string) => {
    const headers = await getAuthHeaders();
    await axios.post(
      `${backendUrl}/api/content-review/cycles/${cycleId}/comments`,
      { issue: text, parentComment: rootId },
      { headers }
    );
    await fetchCycle();
  };

  const handleSubmitAssessment = async () => {
    if (!riskLevel) return;
    setAssessmentError(null);

    // Phase 11.2 — parent-child enforcement: every child cycle must be
    // explicitly marked reviewed by the current user via the per-child panel.
    // Comments alone don't count — leaving a single comment isn't proof the
    // reviewer is done with that child.
    if (data) {
      try {
        const headers = await getAuthHeaders();
        const parsedRes = await axios.get(`${backendUrl}/api/content-review/cycles/${cycleId}/parsed-content`, { headers });
        const children = parsedRes.data.children || [];
        if (children.length > 0) {
          const unreviewed: string[] = [];
          for (const child of children) {
            const childRes = await axios.get(`${backendUrl}/api/content-review/cycles/${child._id}`, { headers });
            const childCycle = childRes.data.cycle;
            const isMarked = (childCycle.reviewedBy || []).some(
              (r: any) => r?.reviewer?.email === user?.email
            );
            if (!isMarked) {
              unreviewed.push(child.contentItem.name);
            }
          }
          if (unreviewed.length > 0) {
            setAssessmentError(`Please mark each embedded item as reviewed first: ${unreviewed.join(', ')}`);
            return;
          }
        }
      } catch (err) {
        // FAIL CLOSED (2026-08-19). This used to log and fall through, so a
        // network blip or a 403 on one child submitted the assessment with
        // embedded items unreviewed — the guard switched itself off in exactly
        // the conditions where it was needed. Blocking on an unverifiable check
        // is the safe direction: the cost is retrying a submission, where the
        // cost of the old behaviour was an unreviewed video reaching approval
        // with nothing recording that it was skipped.
        //
        // The backend now enforces this rule too, so this is no longer the only
        // thing standing between an accidental skip and an approval.
        console.error('Child review check failed:', err);
        setAssessmentError(
          "Couldn't verify that every embedded item has been reviewed, so the assessment was not submitted. Check your connection and try again."
        );
        return;
      }
    }

    try {
      setSubmittingAssessment(true);
      const headers = await getAuthHeaders();
      await axios.put(`${backendUrl}/api/content-review/cycles/${cycleId}/assessments`, {
        riskLevel,
        summary: assessmentSummary.trim()
      }, { headers });
      setReviewCompleted(true);
      setDraftRestored(false);
      setEditingAssessment(false);
      clearDraft(cycleId, user?.email);

      // 2026-08-20 (Fredrik) — completing your review IS the handover.
      //
      // Until now these were two separate acts: this button wrote the
      // assessment, and a second "Mark my step complete" button advanced the
      // sequence. A reviewer who pressed only the first saw nothing move —
      // the status pill stayed 'in review' (correct: it waits for ALL
      // reviewers) and the step indicator stayed on their own step, because
      // it reads stepCompletions, which only that other button wrote. The
      // cycle sat blocked on a step its owner believed they had finished.
      //
      // Gated on the step actually being OURS and NEXT, mirroring
      // MarkStepCompleteButton's own render gate: submitting out of order
      // must not complete a step ahead of the reviewer whose turn it is.
      // The endpoint is idempotent, so re-submitting an assessment is safe.
      let handedOverTo: string | null = null;
      const cyc = data?.cycle;
      // reviewerHandoverStep, not isYourStep directly: the producer step is
      // never handed over from here, even by someone who owns it. See the
      // helper — it carries a read-back this form does not collect, and it has
      // exactly one home.
      const step = cyc ? reviewerHandoverStep(cyc, currentUserStep, user?.email) : null;
      if (cyc && step) {
        try {
          await axios.post(
            `${backendUrl}/api/content-review/cycles/${cycleId}/steps/${step}/complete`,
            {}, { headers }
          );
          const seq = [...(cyc.reviewSequence || [])].sort((a, b) => a.order - b.order);
          const done = new Set([...(cyc.stepCompletions || []).map(sc => sc.step), step]);
          const nextStep = seq.find(s => !done.has(s.step));
          handedOverTo = nextStep ? (STEP_LABEL[nextStep.step as keyof typeof STEP_LABEL] || null) : null;
        } catch (stepErr: any) {
          // Non-blocking: the assessment IS saved. Surfacing rather than
          // swallowing, because the cycle is now in the state this change
          // exists to prevent — submitted but not handed over — and the
          // reviewer is the only one who can still fix it from here.
          setAssessmentError(
            'Your assessment was saved, but your review step could not be marked complete, ' +
            'so the next reviewer has not been notified. Use "Mark step complete" to hand over.'
          );
          setSubmittingAssessment(false);
          await fetchCycle();
          return;
        }
      }

      onBack(
        handedOverTo
          ? `Review submitted — handed over to ${handedOverTo}.`
          : 'Review submitted — awaiting the decision maker.'
      );
    } catch (err: any) {
      setAssessmentError(err.response?.data?.error || 'Failed to submit assessment');
    } finally {
      setSubmittingAssessment(false);
    }
  };

  if (loading) return <div className="content-review" style={{ textAlign: 'center', padding: 40 }}>Loading...</div>;
  if (!data) return <div className="content-review"><p>Review cycle not found.</p></div>;

  const { cycle, comments } = data;

  // The server's verdict for THIS cycle beats the caller's global flag: a
  // producer opening their own review is a participant here even though no
  // mention made them one (2026-08-21).
  const readOnlyParticipant = simulateProducer || participant || data.access === 'participant';

  // Which KIND of participant? The server reports 'participant' for both the
  // mentioned reader and the named producer (requireCycleAccess), and the two
  // want opposite things: the mentioned reader is here to answer a question
  // and may start a thread; the producer is here to read the reviewers' work
  // and give the decision maker a commercial read-back, and may not start one
  // at all ("dialogue starts downstream, never upstream" — Fredrik,
  // 2026-08-24). Matching on the producer field needs no new API surface;
  // cycle.producer is denormalized {teamMemberId, name, email} per D3.
  // WHO the viewer is on this cycle. Person-derived and per-cycle, the same
  // rule isYourStep uses for the producer step.
  const namedProducer = simulateProducer || viewerIsNamedProducer(cycle, user?.email);
  // HOW MUCH of it they may see and do. The two were one boolean until
  // 2026-08-31, which stranded a producer who is also a full-access reviewer:
  // she is not a read-only participant, so the read-back panel never mounted
  // and the producer step had no control anywhere on the page. The read-only
  // behaviours below (reply-only composer, the producer's child panel, the
  // trimmed comment view) still belong to this one — she is a real reviewer
  // here and keeps the reviewer's affordances alongside the read-back.
  const producerView = readOnlyParticipant && namedProducer;
  // The escape valve is NOT here. It lived in this view for about an hour on
  // 2026-08-31 and was removed the same day: OpenStepPanel in DecisionView owns
  // standing in for someone else's step, and rendering it in both places is the
  // same two-homes mistake this day's work exists to fix. This view is where
  // you do YOUR review; the DM view is where you decide, which is where the
  // approve gate bites and therefore where the way past it belongs.

  // Phase 6A — apply the same filter to the Comment Summary that DocumentViewer
  // applies to inline markers (DocumentViewer filters internally via its prop).
  // Replies are never listed as findings in their own right — they belong to
  // their root's thread. Filtering happens on ROOTS only, so a reply is shown
  // or hidden with the conversation it belongs to; filtering replies
  // independently would strand an answer under a hidden question, or hide
  // half a conversation from the person reading the other half.
  const isReply = (c: ReviewComment) => !!c.parentComment;

  const filterByVisibility = (list: ReviewComment[]): ReviewComment[] => {
    // Spec §8 — consultation threads live ONLY in the Consultations panel
    // (Fredrik: "it should all be in the consultations section to keep it
    // clean"). The summary is the findings list; a question is not a finding.
    const roots = list.filter(c => !isReply(c) && c.kind !== 'consultation');
    if (!user?.email || commentVisibility === 'all') return roots;
    if (commentVisibility === 'mine') return roots.filter(c => c.reviewer.email === user.email);
    return roots.filter(c => c.reviewer.email !== user.email);
  };

  // rootId -> replies, oldest first (the API sorts newest-first for the list).
  const repliesByRoot = (() => {
    const map: Record<string, ReviewComment[]> = {};
    for (const c of [...comments, ...childComments]) {
      if (!isReply(c)) continue;
      const key = String(c.parentComment);
      (map[key] = map[key] || []).push(c);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
    }
    return map;
  })();

  // The parent list is gone with the Comment Summary — the viewer applies the
  // same visibility filter itself and hands the rail the result, so filtering
  // a second copy here would only give the two lists a chance to disagree.
  const visibleChildComments = filterByVisibility(childComments);

  // Counts use the unfiltered set so toggle labels show what's available
  const mineCount = user?.email
    ? comments.filter(c => c.reviewer.email === user.email).length
      + childComments.filter(c => c.reviewer.email === user.email).length
    : 0;
  const totalCount = comments.length + childComments.length;
  const othersCount = totalCount - mineCount;

  // Group child comments by their source name
  const childCommentsBySource: Record<string, ReviewComment[]> = {};
  visibleChildComments.forEach(c => {
    const name = (c as any)._childName || 'Linked content';
    if (!childCommentsBySource[name]) childCommentsBySource[name] = [];
    childCommentsBySource[name].push(c);
  });

  return (
    <div className="content-review">
      {/* Wrapped, not passed bare: onBack now takes an optional flash string,
          and onClick would hand it the MouseEvent as that argument. */}
      <button className="review-back-btn" onClick={() => onBack()}>&#8592; Back to Dashboard</button>

      <h2>{cycle.contentItem.name} <GuideLink href={producerView ? ROLE_GUIDES.producer : ROLE_GUIDES.reviewer} label={producerView ? 'Producer guide' : 'Reviewer guide'} /></h2>
      <RevisionBanner cycle={cycle} getAuthHeaders={getAuthHeaders} />

      {/* Producer's upload notes — visible to reviewers, not just the DM at
          prescreen (Fredrik, 2026-09-02, medical-reviewer walk). */}
      {cycle.submissionNotes && (
        <div style={{ background: '#f0f7ff', padding: 14, borderRadius: 8, borderLeft: '3px solid #123250', marginBottom: 16, fontSize: 14, color: '#333' }}>
          <strong style={{ fontSize: 12, color: '#123250', display: 'block', marginBottom: 4 }}>Submission Notes</strong>
          {cycle.submissionNotes}
        </div>
      )}
      {/* 2026-09-01 (Fredrik, testing Producer view) — the producer standing
          inside a revision_needed cycle gets the re-upload HERE, not only in
          the My Submissions rows. */}
      {/* The spec is FOR the producer — it must sit where they act on it:
          above the re-upload after "revision needed", and above Confirm fixes
          on an approved-held cycle (Fredrik, 2026-09-01 — it lived only in
          DecisionView, invisible from this view). autoLoad: here the spec is
          the work list, not an optional detail. */}
      {producerView && (cycle.status === 'revision_needed' || cycle.pendingFixes) && (
        <ChangeSpecPanel cycleId={cycle._id} getAuthHeaders={getAuthHeaders} autoLoad />
      )}
      {producerView && cycle.status === 'revision_needed' && (
        <SubmitRevisionControl cycle={cycle} getAuthHeaders={getAuthHeaders} onSubmitted={fetchCycle} />
      )}
      {/* Phase 12 fix pass Batch B (Finding 2) — surface producer.name so
          reviewers can see who submitted the file without cross-referencing
          the DB. Only renders when producer field is populated (Phase 8D+ cycles). */}
      {cycle.producer?.name && (
        <div style={{ fontSize: 13, color: '#6c757d', marginTop: -8, marginBottom: 12 }}>
          Producer: <span style={{ color: '#495057', fontWeight: 500 }}>{cycle.producer.name}</span>
        </div>
      )}
      {/* WS1.8 (walk feedback: "styling and alignment should be worked on")
          — the header is three grouped rows instead of one crowded wrap:
          1. identity/meta: status + tags + deadline
          2. sequence: position strip on its own line
          3. actions: lock + step-complete + done-iterating, right-aligned */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className={`review-status-badge ${cycle.status}`}>
            {cycle.status.replace(/_/g, ' ')}
          </span>
          {cycle.contentItem.type && <span className="review-meta-tag purpose">{cycle.contentItem.type}</span>}
          {cycle.contentItem.audience && <span className="review-meta-tag audience">{cycle.contentItem.audience}</span>}
          {cycle.contentItem.version && <span className="review-meta-tag version">{cycle.contentItem.version}</span>}
          <DeadlinePill deadline={cycle.reviewDeadline} />
          {reviewCompleted && cycle.status !== 'approved' && cycle.status !== 'revision_needed' && cycle.status !== 'rejected' && (
            <span className="review-status-badge submitted">
              Submitted — awaiting decision
            </span>
          )}
        </div>
        {/* Phase 12 fix pass Batch B — sequence position awareness. */}
        <div>
          <ReviewSequenceIndicator cycle={cycle} currentUserStep={currentUserStep} currentUserEmail={user?.email || null} />
        </div>
        {/* The edit lock lived here until 2026-08-21; it is gone (advisory
            only, recorded nothing — see routes/contentReview.js). Step
            completion now sits with the Risk Assessment below, which is the
            act it belongs to. */}
        {/* DoneIteratingButton left this view 2026-09-01 (Fredrik): the
            producer owns the revision loop, and a reviewer clicking it on a
            file cycle would send the UNCHANGED file back to review. The
            control lives in DecisionView. */}
      </div>

      {/* "Approved, pending fixes" — the cycle is decided and held; confirming
          is what ships it. Mounted HERE, above everything and OUTSIDE the
          `!readOnlyParticipant` block, for the reason recorded in
          ConfirmFixesPanel: the producer who presses it IS a read-only
          participant, and both controls previously placed inside that block
          were unreachable by the people they were built for. `canConfirm`
          decides who gets the button; the mount decides nobody. The backend
          re-checks either way. */}
      {cycle.pendingFixes && activeTab === 'parent' && (
        <ConfirmFixesPanel
          cycleId={cycle._id}
          amendment={(cycle as any).amendment}
          canConfirm={namedProducer || !readOnlyParticipant}
          onConfirmed={fetchCycle}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {readOnlyParticipant && (
        <div data-testid="participant-banner" style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 8, background: '#f0f7ff', border: '1px solid #b8daff', fontSize: 13, color: '#123250' }}>
          {producerView ? (
            <>
              <strong>This is your content.</strong> You can read every finding, suggested fix
              and thread, and reply where someone has asked you something. You don't file
              findings or start new threads with the reviewers — your channel is the
              commercial perspective below, which goes to the decision maker.
            </>
          ) : (
            <>
              <strong>You were mentioned on this review.</strong> You can read the content and every
              thread, reply, and ask others for input — but not file findings or
              complete review steps.
            </>
          )}
        </div>
      )}

      {error && <div style={{ color: '#dc3545', padding: 12, background: '#f8d7da', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* AI Pre-Review */}
      {cycle.aiPreReview && (
        <div className="ai-prereview">
          <h4>AI Pre-Review Briefing</h4>
          <p>{cycle.aiPreReview}</p>
        </div>
      )}

      {/* Document Viewer (if content is parsed) */}
      {cycle.contentType && (
        <div className="review-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Content</h3>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <span style={{ fontSize: 11, color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>
                Filter comments
              </span>
              <CommentVisibilityToggle
                value={commentVisibility}
                onChange={setCommentVisibility}
                mineCount={mineCount}
                totalCount={totalCount}
                othersCount={othersCount}
              />
            </div>
          </div>
          <DocumentViewer
            user={user}
            cycle={cycle}
            comments={comments}
            getAuthHeaders={getAuthHeaders}
            onCommentAdded={fetchCycle}
            refreshKey={dataVersion}
            composerMode={producerView ? 'reply-only' : readOnlyParticipant ? 'consultation' : 'full'}
            canEditAnyComment={canEditAnyComment}
            // The Comment Summary that used to sit below this viewer now IS
            // the rail's comments tab. The card is unchanged and still lives
            // here — only its container moved, so resolve, reply, edit and
            // the mention treatment keep working exactly as they did.
            renderCommentDetail={(c) => (
              <CommentCard
                comment={c}
                onToggleResolved={handleToggleResolved}
                viewerEmail={user?.email}
                replies={repliesByRoot[c._id] || []}
                onReply={handleReplyToComment}
                editorEmail={user?.email}
                canEditAny={canEditAnyComment}
                onEdited={fetchCycle}
                getAuthHeaders={getAuthHeaders}
                inRail
              />
            )}
            commentVisibility={commentVisibility}
            activeTab={activeTab}
            onActiveTabChange={setActiveTab}
          />
        </div>
      )}

      {/* Phase 11.2 — when a child tab is active, replace the Risk Assessment
          form with a lightweight "Mark as reviewed" panel for that child. The
          full assessment lives at the parent level. */}
      {/* The producer's child tab (Fredrik, 2026-08-24). Same mechanic as the
          reviewers' panel below and the same storage (reviewedBy), different
          meaning and therefore different words: a reviewer inspects the item,
          the producer reads the FEEDBACK on it. Without this the producer could
          give a commercial read-back having never opened the tab where half the
          findings live — and the tab said nothing, so there was no way to know
          it mattered. */}
      {producerView && activeTab !== 'parent' && childCycles[activeTab] && (() => {
        const childCycle = childCycles[activeTab];
        const childName = childCycle.contentItem.name;
        const feedbackCount = childComments.filter(
          c => (c as any)._childName === childName && c.reviewer.email !== user?.email
        ).length;
        const isRead = (childCycle.reviewedBy || []).some(
          r => r?.reviewer?.email?.toLowerCase() === (user?.email || '').toLowerCase()
        );
        return (
          <div className="review-section" data-testid="producer-child-panel">
            <h3>Feedback on: {childName}</h3>
            <div className="viewer-tab-hint">
              This item is reviewed separately from the main content, so the findings on it
              live here. Read them, reply to anything addressed to you, then confirm below —
              you can't mark the review ready to distribute until every embedded item is done.
            </div>
            {isRead ? (
              <div style={{ background: '#d4edda', padding: 14, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 18, color: '#155724' }}>&#10003;</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <strong style={{ color: '#155724' }}>You've read the feedback on this item.</strong>
                  <p style={{ margin: '2px 0 0', fontSize: 13, color: '#555' }}>
                    {feedbackCount > 0
                      ? `${feedbackCount} comment${feedbackCount === 1 ? '' : 's'} from the reviewers on this item.`
                      : 'The reviewers left nothing on this item.'}
                  </p>
                </div>
                <button
                  className="review-btn review-btn-outline"
                  onClick={() => handleToggleChildReviewed(activeTab, false)}
                  disabled={markingReviewed}
                  style={{ fontSize: 13 }}
                >
                  Undo
                </button>
              </div>
            ) : (
              <>
                <p style={{ fontSize: 13, color: '#555', margin: '0 0 10px' }}>
                  {feedbackCount > 0
                    ? `${feedbackCount} comment${feedbackCount === 1 ? '' : 's'} from the reviewers on this item.`
                    : 'The reviewers left nothing on this item — confirming still records that you looked.'}
                </p>
                <button
                  className="review-btn review-btn-success"
                  onClick={() => handleToggleChildReviewed(activeTab, true)}
                  disabled={markingReviewed}
                  style={{ fontSize: 14, padding: '8px 18px' }}
                >
                  {markingReviewed ? 'Saving...' : "I've read the feedback on this item"}
                </button>
              </>
            )}
          </div>
        );
      })()}

      {!readOnlyParticipant && activeTab !== 'parent' && childCycles[activeTab] && (() => {
        const childCycle = childCycles[activeTab];
        const myComments = childComments.filter(
          c => c.reviewer.email === user?.email && (c as any)._childName === childCycle.contentItem.name
        );
        // Strict: only an explicit Mark-as-reviewed click counts. Comments
        // don't auto-mark — a reviewer leaving a single comment isn't proof
        // they're done with the item.
        const isReviewed = (childCycle.reviewedBy || []).some(
          r => r?.reviewer?.email === user?.email
        );
        return (
          <div className="review-section">
            <h3>Reviewed: {childCycle.contentItem.name}</h3>
            {isReviewed ? (
              <div style={{ background: '#d4edda', padding: 14, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 18, color: '#155724' }}>&#10003;</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <strong style={{ color: '#155724' }}>You've marked this as reviewed.</strong>
                  <p style={{ margin: '2px 0 0', fontSize: 13, color: '#555' }}>
                    {myComments.length > 0
                      ? `You have ${myComments.length} comment${myComments.length === 1 ? '' : 's'} on this item.`
                      : 'No comments needed — you confirmed there\'s nothing to flag.'}
                  </p>
                </div>
                <button
                  className="review-btn review-btn-outline"
                  onClick={() => handleToggleChildReviewed(activeTab, false)}
                  disabled={markingReviewed}
                  style={{ fontSize: 13 }}
                >
                  Undo
                </button>
              </div>
            ) : (
              <div style={{ background: '#fff8e1', padding: 14, borderRadius: 8 }}>
                <p style={{ margin: '0 0 10px', fontSize: 14, color: '#856404' }}>
                  When you've watched / listened to this {childCycle.contentType || 'item'} and added any
                  comments you want, mark it as reviewed.
                </p>
                <button
                  className="review-btn review-btn-primary"
                  onClick={() => handleToggleChildReviewed(activeTab, true)}
                  disabled={markingReviewed}
                  style={{ fontSize: 14 }}
                >
                  {markingReviewed ? 'Saving…' : 'Mark as reviewed'}
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* The producer's slot — same place, same moment in the page as the
          reviewers' Risk Assessment, because it is the same act: the person
          whose turn it is finishes their turn here. Parent tab only, matching
          the assessment; an embedded item is not a separate hand-over.

          This is also the fix for the producer having no control at all: the
          only mount of MarkStepCompleteButton a producer could reach sat
          inside the `!readOnlyParticipant` block below, and the producer-access
          grant (2026-08-21) made every producer a participant — so "Mark ready
          to distribute" existed, was permitted by the backend, and never
          rendered. */}
      {namedProducer && activeTab === 'parent' && (
        <ProducerReadbackPanel
          cycle={cycle}
          getAuthHeaders={getAuthHeaders}
          onSubmitted={fetchCycle}
          // The hand-over ends the producer's turn, so it ends their time on
          // this page — same as "Complete Review" does for a reviewer. A plain
          // perspective update still just refetches (onSubmitted).
          onHandedOver={flash => onBack(flash)}
          // Embedded items the producer has not yet confirmed reading. The
          // backend refuses the hand-over on these (CHILDREN_NOT_REVIEWED);
          // the panel names them so the refusal is never a surprise.
          unreadItems={Object.values(childCycles)
            .filter(kid => !(kid.reviewedBy || []).some(
              r => r?.reviewer?.email?.toLowerCase() === (user?.email || '').toLowerCase()
            ))
            .map(kid => kid.contentItem.name)}
          onOpenItem={setActiveTab}
          childIdByName={Object.fromEntries(
            Object.entries(childCycles).map(([id, kid]) => [kid.contentItem.name, id])
          )}
        />
      )}

      {/* Read-only for everyone else with review access (Fredrik, 2026-08-24:
          the commercial read is not DM-only). Nothing to show until the
          producer has actually submitted one. */}
      {!namedProducer && activeTab === 'parent' && cycle.producerReadback?.submittedAt && (
        <div className="review-section" data-testid="producer-readback-readonly">
          <h3>Producer — commercial perspective</h3>
          <p style={{ fontSize: 12, color: '#888', margin: '0 0 8px' }}>
            The producer's read on this piece, addressed to the decision maker. Not a risk assessment.
          </p>
          <div style={{ fontSize: 14, color: '#333', whiteSpace: 'pre-wrap' }}>
            {cycle.producerReadback.text}
          </div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
            {cycle.producerReadback.submittedBy?.name || 'Producer'} &middot;{' '}
            {new Date(cycle.producerReadback.submittedAt).toLocaleString()}
          </div>
        </div>
      )}

      {/* Risk Assessment — only on the parent tab, and only for this cycle's
          OWN reviewers (2026-08-31). It used to render for any full-access
          user, which put an editable form in front of people whose submission
          the backend refuses — a decision maker saw their own months-old local
          draft here and reasonably read it as the reviewer's submitted
          assessment. A DM who needs to file a review uses the DM view's
          stand-in path, which records it against a step. */}
      {!readOnlyParticipant && isAssignedReviewer(cycle, user?.email) && activeTab === 'parent' && (() => {
        const childCount = Object.keys(childCycles).length;
        const isDecided = cycle.status === 'approved' || cycle.status === 'revision_needed' || cycle.status === 'rejected';
        const isLocked = isDecided;
        // Server truth, not the `reviewCompleted` flag: the card shows what was
        // actually recorded, so it can never display an unsent draft as though
        // it had been submitted.
        const mine = (cycle.reviewerAssessments || []).find(a =>
          (a.reviewer?.email || '').toLowerCase() === (user?.email || '').toLowerCase() && a.submittedAt);
        const showCard = !!mine && !editingAssessment;
        const riskLabel = mine
          ? (RISK_OPTIONS.find(o => o.value === mine.riskLevel)?.label || mine.riskLevel || 'not set')
          : '';

        return (
          <div className="review-section">
            <h3>Your Risk Assessment</h3>

            {/* 2026-08-19 (Fredrik): the child tabs explained what to do there,
                but the main tab explained nothing — and where children exist the
                assessment below covers the MAIN content only, while each
                embedded item is marked separately. Reviewers had no way to know
                that until submit failed. */}
            <div className="viewer-tab-hint">
              This assessment covers the <strong>main content</strong>
              {childCount > 0 ? (
                <> only. The {childCount} embedded item{childCount === 1 ? '' : 's'} in
                the tabs above {childCount === 1 ? 'is' : 'are'} marked reviewed separately,
                and all of them must be done before you can submit this.</>
              ) : (
                <>. Read through it, add comments on anything that needs flagging, then set a risk
                level and summarise below.</>
              )}
            </div>

            {reviewCompleted && !isDecided && (
              <div style={{ background: '#e0e7ff', padding: 14, borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18, color: '#3730a3' }}>&#10003;</span>
                <div>
                  <strong style={{ color: '#3730a3' }}>Submitted — awaiting decision</strong>
                  <p style={{ margin: '2px 0 0', fontSize: 13, color: '#555' }}>
                    You can still update your assessment until the decision maker makes a final decision.
                  </p>
                </div>
              </div>
            )}

            {isDecided && (
              <div style={{ background: cycle.status === 'approved' ? '#d4edda' : '#f8d7da', padding: 14, borderRadius: 8, marginBottom: 16 }}>
                <strong style={{ color: cycle.status === 'approved' ? '#155724' : '#721c24' }}>
                  Final decision: {cycle.status.replace(/_/g, ' ')}
                </strong>
              </div>
            )}

            {/* SUBMITTED — a read-only record with an explicit way in. It used
                to be a permanently hot form: one stray click on a green button
                overwrote a recorded assessment, and there was no way to tell
                the recorded text from unsent local draft text sitting in the
                same boxes. */}
            {showCard ? (
              <>
                <div
                  data-testid="assessment-submitted-card"
                  style={{ border: '1px solid #d9dee5', borderRadius: 8, padding: 14, marginBottom: 12, background: '#fbfcfd' }}
                >
                  <div style={{ fontSize: 13, color: '#55606d' }}>Risk level</div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: mine!.summary ? 10 : 0 }}>{riskLabel}</div>
                  {mine!.summary && (
                    <>
                      <div style={{ fontSize: 13, color: '#55606d' }}>Summary</div>
                      <div style={{ fontSize: 14, color: '#333', whiteSpace: 'pre-wrap' }}>{mine!.summary}</div>
                    </>
                  )}
                  <div style={{ fontSize: 12, color: '#55606d', marginTop: 10 }}>
                    Submitted{mine!.reviewer?.name ? ` by ${mine!.reviewer.name}` : ''} &middot;{' '}
                    {new Date(mine!.submittedAt as any).toLocaleString()}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  {!isLocked && (
                    <button
                      className="review-btn review-btn-outline"
                      onClick={() => setEditingAssessment(true)}
                      data-testid="assessment-edit"
                      style={{ fontSize: 14, padding: '8px 18px' }}
                    >
                      Edit assessment
                    </button>
                  )}
                  {/* Still reachable from the card. Its one surviving purpose
                      is the reviewer who submitted OUT OF TURN and must hand
                      over when their step comes round — hiding it behind Edit
                      would strand exactly that person. */}
                  <MarkStepCompleteButton
                    cycle={cycle}
                    currentUserStep={currentUserStep}
                    userEmail={user?.email}
                    getAuthHeaders={getAuthHeaders}
                    onComplete={fetchCycle}
                  />
                </div>
              </>
            ) : (
            <>
            {/* UNSENT DRAFT — say so. This text lives in this browser only and
                has never reached the server. */}
            {draftRestored && !mine && (
              <div
                data-testid="assessment-draft-notice"
                style={{ padding: '10px 14px', background: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#664d03' }}
              >
                <strong>Draft &mdash; not submitted.</strong> Restored from this browser. Nobody
                else can see it, and it is not part of the review until you complete it below.
              </div>
            )}
            <div className="review-form-group" style={{ maxWidth: 240 }}>
              {/* htmlFor/id, not a bare <label> (2026-08-26). Axe rates an
                  unnamed select CRITICAL — a screen reader announces "combo
                  box" with no idea what it sets, and this is the control that
                  carries the reviewer's verdict. The bare-label pattern is
                  used throughout this module (52 labels, 0 htmlFor); this is
                  the one a scan actually reached. */}
              <label htmlFor="reviewer-risk-level">Risk Level</label>
              <select
                id="reviewer-risk-level"
                value={riskLevel}
                onChange={e => setRiskLevel(e.target.value as RiskLevel)}
                disabled={isLocked}
              >
                <option value="">Select...</option>
                {RISK_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="review-form-group">
              <label>Summary (optional)</label>
              <textarea
                value={assessmentSummary}
                onChange={e => setAssessmentSummary(e.target.value)}
                placeholder="Brief summary of your overall assessment..."
                rows={2}
                disabled={isLocked}
              />
            </div>

            {assessmentError && (
              <div style={{ color: '#dc3545', padding: 12, background: '#f8d7da', borderRadius: 8, marginBottom: 12, fontSize: 14 }}>
                {assessmentError}
              </div>
            )}

            {!isLocked && (
              <>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    className="review-btn review-btn-success"
                    onClick={handleSubmitAssessment}
                    disabled={!riskLevel || submittingAssessment}
                    style={{ fontSize: 15, padding: '10px 24px' }}
                  >
                    {submittingAssessment ? 'Submitting...' : mine ? 'Update assessment' : 'Complete Review'}
                  </button>
                  {/* Step completion sits WITH the assessment (Fredrik,
                      2026-08-21) — it is the same act from the user's side,
                      and it used to be stranded in the header next to the
                      edit lock. Self-gating: nothing renders unless this
                      user's step is next, and for medical/legal not until
                      their assessment is submitted, so the common path shows
                      only the button above. The producer, who has no
                      assessment to submit, sees "Mark ready to distribute"
                      here — the same place reviewers finish. */}
                  <MarkStepCompleteButton
                    cycle={cycle}
                    currentUserStep={currentUserStep}
                    userEmail={user?.email}
                    getAuthHeaders={getAuthHeaders}
                    onComplete={fetchCycle}
                  />
                </div>
                {/* #888 was 3.54:1 on white — below AA for 12px text. */}
                {!reviewCompleted && (
                  <p style={{ fontSize: 12, color: '#55606d', marginTop: 8 }}>
                    Completing your review submits your risk assessment and inline comments to the decision maker.
                  </p>
                )}
              </>
            )}
            </>
            )}
          </div>
        );
      })()}

      {/* WS1.5 — Consultations audit trail, previously DM-only (PreScreenView +
          DecisionView). Reviewers can @mention but couldn't see the trail from
          their own view. Rows link down into the Comment Summary below. */}
      {/* 2026-08-26 — ConsultationsPanel moved INTO the viewer's right rail
          (ViewerSidePanel). It was here, below the document, so answering a
          question meant scrolling past the whole item and then losing sight
          of the content you were answering about. Rendering it in both places
          would double-fetch and give two sources of truth for one thread. */}

      {/* The Comment Summary lived here until 2026-08-26. It is the rail's
          comments tab now (ViewerSidePanel → CommentsRail), for the same
          reason the consultations moved a day earlier: a finding is read
          AGAINST the passage it is about, and a list below the document put
          the two several screens apart. Rendering it in both places would
          duplicate `id="comment-<id>"` and break every marker click. */}
    </div>
  );
};

interface CommentVisibilityToggleProps {
  value: CommentVisibility;
  onChange: (v: CommentVisibility) => void;
  mineCount: number;
  totalCount: number;
  othersCount: number;
}

const CommentVisibilityToggle: React.FC<CommentVisibilityToggleProps> = ({
  value, onChange, mineCount, totalCount, othersCount
}) => {
  const options: { value: CommentVisibility; label: string; count: number }[] = [
    { value: 'mine', label: 'Mine only', count: mineCount },
    { value: 'all', label: 'All', count: totalCount },
    { value: 'others', label: 'Others only', count: othersCount }
  ];
  return (
    <div role="radiogroup" aria-label="Comment visibility" style={{ display: 'inline-flex', border: '1px solid #d0d7de', borderRadius: 6, overflow: 'hidden', fontSize: 12, lineHeight: 1 }}>
      {options.map(opt => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            style={{
              padding: '4px 9px',
              margin: 0,
              border: 'none',
              borderRight: opt.value !== 'others' ? '1px solid #d0d7de' : 'none',
              background: active ? '#123250' : '#fff',
              color: active ? '#fff' : '#123250',
              cursor: 'pointer',
              font: 'inherit',
              fontWeight: active ? 600 : 400,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              display: 'inline-block'
            }}
          >
            {opt.label}<span style={{ opacity: 0.7, marginLeft: 4 }}>{opt.count}</span>
          </button>
        );
      })}
    </div>
  );
};

// Shared comment card
interface CommentCardProps {
  comment: ReviewComment;
  sourceName?: string;
  onToggleResolved: (comment: ReviewComment) => void;
  // When set and the comment mentions this address, the card is flagged as
  // addressed to the reader (2026-08-20).
  viewerEmail?: string | null;
  // Thread replies (COMMENT_DISPOSITION_SPEC §4). Omitted by callers that
  // render a comment outside its thread context.
  replies?: ReviewComment[];
  onReply?: (rootId: string, text: string) => Promise<void>;
  getAuthHeaders?: () => Promise<Record<string, string>>;
  /** Email of the signed-in user — gates the Edit affordance to the author. */
  editorEmail?: string | null;
  /** Decision makers may edit anyone's comment; mirrors the backend rule. */
  canEditAny?: boolean;
  /** Called after a successful edit so the caller can refetch. */
  onEdited?: () => void;
  /**
   * Rendered inside a rail row (2026-08-26). Two things must change and both
   * are correctness, not cosmetics:
   *
   * 1. NO `id="comment-<id>"`. The row already carries it, and openComment
   *    resolves by getElementById — two elements with one id means the
   *    marker click silently lands on whichever the DOM hands back first.
   * 2. No author, severity, mention or resolved badge. The row shows all four
   *    while COLLAPSED, which is the point of it; repeating them two lines
   *    below is noise in a 360px column.
   */
  inRail?: boolean;
}

export const CommentCard: React.FC<CommentCardProps> = ({
  comment, onToggleResolved, viewerEmail, replies = [], onReply, getAuthHeaders,
  editorEmail, canEditAny = false, onEdited, inRail = false
}) => {
  // Discussion lives on consultations (2026-08-21, Fredrik): a finding is a
  // statement to the decision maker, answered by a disposition rather than by
  // a conversation. Existing finding threads stay readable — the backend
  // refuses only NEW replies onto them — so this hides the box, not history.
  const isConsultation = (comment.kind || 'finding') === 'consultation';
  const isAuthor = !!editorEmail &&
    comment.reviewer?.email?.toLowerCase() === editorEmail.toLowerCase();
  const canEdit = canEditAny || isAuthor;
  const [editing, setEditing] = React.useState(false);

  const forMe = mentionsViewer(comment, viewerEmail);
  const [replyText, setReplyText] = React.useState('');
  // Open by default when the reader was mentioned and nobody has answered yet
  // — the whole point of flagging a mention is that a reply is expected, so
  // making them hunt for the box would reintroduce the friction.
  const [replyOpen, setReplyOpen] = React.useState(forMe && replies.length === 0);
  const [sending, setSending] = React.useState(false);
  const [replyError, setReplyError] = React.useState<string | null>(null);

  const submitReply = async () => {
    const text = replyText.trim();
    if (!text || !onReply) return;
    setSending(true);
    setReplyError(null);
    try {
      await onReply(comment._id, text);
      setReplyText('');
      setReplyOpen(false);
    } catch (err: any) {
      setReplyError(err?.response?.data?.error || 'Could not post the reply. Try again.');
    } finally {
      setSending(false);
    }
  };

  return (
  // id anchor — WS1.6: ConsultationsPanel "Open comment" scroll target, and
  // WS1.7: the in-document marker click target.
  <div
    id={inRail ? undefined : `comment-${comment._id}`}
    className={`comment-item ${comment.severity} ${comment.resolved ? 'resolved' : ''}${inRail ? ' in-rail' : ''}`}
    // A comment that mentions you is a question addressed to you, not another
    // reviewer's opinion — it reads identically to every other card otherwise,
    // which is how Rita's went unnoticed in the 2026-08-20 prod run. Ring +
    // tint + an explicit badge, and only while it is unresolved: once resolved
    // it is history and should stop shouting.
    style={forMe && !comment.resolved && !inRail
      ? { boxShadow: 'inset 0 0 0 2px #7c3aed', background: '#f5f3ff', borderRadius: 8 }
      : undefined}
  >
    <div className="comment-header">
      {/* The meta group. In a rail row the author, severity, mention and
          resolved badges are all shown by the ROW, so this group is left
          holding nothing but its own inline `display: flex` — which no
          stylesheet can collapse, and `justify-content: space-between` then
          pushed the actions into a band of dead space above every open
          comment. Render it only when it has something to say. */}
      {(!inRail || comment.timestamp !== undefined
        || (comment.stateLabel && comment.stateId && comment.stateId !== 'default')
        || (carriesDisposition(comment) && comment.disposition && comment.disposition !== 'undecided')) && (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {!inRail && <span className="comment-reviewer">{comment.reviewer.name}</span>}
        {forMe && !inRail && (
          <span
            style={{
              // Violet, matching the in-document markers — amber was the
              // minor-severity colour and made the badge read as a finding
              // level (the same collision fixed on the overlays, §10).
              background: '#7c3aed', color: '#fff', fontSize: 11, fontWeight: 700,
              padding: '2px 8px', borderRadius: 10, letterSpacing: 0.2
            }}
            title="You were @mentioned in this comment — a reply is expected"
          >
            {comment.resolved ? 'Mentioned you' : 'Mentions you — reply expected'}
          </span>
        )}
        {!inRail && <span className={`severity-badge ${comment.severity}`}>{comment.severity}</span>}
        {comment.timestamp !== undefined && (
          <span style={{ fontSize: 11, color: '#666', background: '#f0f0f0', padding: '1px 8px', borderRadius: 4 }}>
            at {Math.floor(comment.timestamp / 60)}:{String(comment.timestamp % 60).padStart(2, '0')}
          </span>
        )}
        {comment.stateLabel && comment.stateId && comment.stateId !== 'default' && (
          <span style={{ fontSize: 11, color: '#123250', background: '#e8f0fa', padding: '1px 8px', borderRadius: 4, fontWeight: 500 }}>
            {comment.stateLabel}
          </span>
        )}
        {comment.resolved && !inRail && <span style={{ color: '#28a745', fontSize: 12, fontWeight: 600 }}>Resolved</span>}
        {/* Spec §2.2 — the DM's disposition, read-only here. Shown only once
            decided; an undecided finding carries no badge in the reviewer view. */}
        {carriesDisposition(comment) && comment.disposition && comment.disposition !== 'undecided' && (
          <span
            style={{ fontSize: 11, padding: '1px 8px', borderRadius: 4, background: '#eaf7ee', color: '#155724', fontWeight: 600 }}
            title={comment.disposition === 'wont_apply' && comment.dispositionReason ? comment.dispositionReason : undefined}
          >
            {DISPOSITION_LABELS[comment.disposition]}
          </span>
        )}
      </div>)}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {canEdit && !editing && (
          <button
            data-testid="comment-edit"
            onClick={() => setEditing(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#123250', fontSize: 12, fontWeight: 600 }}
          >
            Edit
          </button>
        )}
        <button
          onClick={() => onToggleResolved(comment)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: comment.resolved ? '#dc3545' : '#28a745',
            fontSize: 12, fontWeight: 600
          }}
        >
          {comment.resolved ? 'Reopen' : 'Resolve'}
        </button>
      </div>
    </div>
    {comment.anchorThumbnail ? (
      <div style={{ margin: '4px 0 6px' }}>
        <img
          src={comment.anchorThumbnail}
          alt="Marked region"
          style={{ maxWidth: '100%', maxHeight: 120, display: 'block', border: '1px solid #ddd', borderRadius: 4 }}
        />
      </div>
    ) : comment.textSelection ? (
      <div style={{ fontSize: 12, color: '#555', fontStyle: 'italic', background: '#f0f7ff', padding: '4px 10px', borderLeft: '3px solid #123250', borderRadius: '0 4px 4px 0', margin: '4px 0 6px', maxHeight: 60, overflowY: 'auto' }}>
        "{comment.textSelection}"
      </div>
    ) : comment.sectionContent ? (
      <div style={{ fontSize: 12, color: '#555', fontStyle: 'italic', background: '#f5f5f5', padding: '4px 10px', borderLeft: '3px solid #999', borderRadius: '0 4px 4px 0', margin: '4px 0 6px', maxHeight: 60, overflowY: 'auto' }}>
        {comment.sectionContent}
      </div>
    ) : null}
    {/* WS1.7 — @tokens render as pills (solid = resolved mention, dashed
        amber = no team-member matched, i.e. a probable typo). */}
    {editing && getAuthHeaders ? (
      <CommentEditForm
        comment={comment}
        getAuthHeaders={getAuthHeaders}
        onSaved={onEdited}
        onCancel={() => setEditing(false)}
        canDelete={isAuthor}
      />
    ) : (
      <p style={{ margin: '4px 0', fontSize: 14 }}>{renderWithMentions(comment.issue, comment.mentionedTeamMembers)}</p>
    )}
    {comment.location && <p style={{ margin: '2px 0', fontSize: 12, color: '#666' }}>Location: {comment.location}</p>}
    {comment.suggestedFix && (
      <p style={{ margin: '4px 0', fontSize: 13, color: '#155724', background: '#d4edda', padding: '6px 10px', borderRadius: 6 }}>
        Suggested fix: {comment.suggestedFix}
      </p>
    )}

    {/* Thread — replies carry no severity and no disposition (spec §4), so
        they render as plain messages rather than a second row of findings. */}
    {replies.length > 0 && (
      <div className="comment-thread">
        {replies.map(r => (
          <div key={r._id} id={`comment-${r._id}`} className="comment-thread-item">
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span className="comment-thread-author">{r.reviewer.name}</span>
              {r.createdAt && (
                <span className="comment-thread-time">
                  {new Date(r.createdAt).toLocaleString()}
                </span>
              )}
            </div>
            <p className="comment-thread-body">
              {renderWithMentions(r.issue, r.mentionedTeamMembers)}
            </p>
          </div>
        ))}
      </div>
    )}

    {onReply && getAuthHeaders && isConsultation && (
      <div style={{ marginTop: 8 }}>
        {!replyOpen ? (
          <button className="comment-reply-open" onClick={() => setReplyOpen(true)}>
            Reply{replies.length > 0 ? ` (${replies.length})` : ''}
          </button>
        ) : (
          <div className="comment-reply-box">
            <MentionTextarea
              value={replyText}
              onChange={setReplyText}
              getAuthHeaders={getAuthHeaders}
              placeholder="Write a reply… use @name to pull someone in"
              rows={2}
            />
            {replyError && (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#dc3545' }}>{replyError}</p>
            )}
            <div className="comment-reply-actions">
              <button
                className="review-btn review-btn-primary"
                onClick={submitReply}
                disabled={!replyText.trim() || sending}
                style={{ fontSize: 12, padding: '5px 14px' }}
              >
                {sending ? 'Posting…' : 'Post reply'}
              </button>
              <button className="comment-reply-open" style={{ color: '#8a94a6' }}
                onClick={() => { setReplyOpen(false); setReplyError(null); }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    )}
  </div>
  );
};

// Re-exported so the mention tests and DocumentViewer have one import
// site; the implementation lives in ./mentions to keep DocumentViewer from
// importing ReviewInterface, which imports DocumentViewer.
export { mentionsViewer };

export default ReviewInterface;
